import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'data-engine-api',
  version: '2.0.0',
  credentials: [
    {
      name: 'data-engine',
      required: true,
    },
  ],
  properties: [
    // ═══════════════════════════════════════════════════════════════════════════
    // CREDENTIAL
    // ═══════════════════════════════════════════════════════════════════════════
    {
      key: 'credential',
      label: 'Data Engine Credential',
      type: 'credential',
      required: true,
      description: 'Select your Data Engine API credential',
      credentialTypes: [
        {
          name: 'data-engine',
          required: true,
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // RESOURCE GROUP SELECTOR
    // ═══════════════════════════════════════════════════════════════════════════
    {
      key: 'resourceGroup',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'data',
      description: 'Select the resource type to work with',
      options: [
        { value: 'data', name: 'Data' },
        { value: 'bulkData', name: 'Bulk Data' },
        { value: 'table', name: 'Table' },
        { value: 'dataFlow', name: 'Data Flow' },
        { value: 'schedule', name: 'Schedule' },
        { value: 'view', name: 'View' },
      ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // OPERATION SELECTORS (conditional based on resourceGroup)
    // ═══════════════════════════════════════════════════════════════════════════

    // Data operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getRows',
      description: 'Select the operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['data'],
        },
      },
      options: [
        { value: 'getRows', name: 'Get Rows' },
        { value: 'insertRows', name: 'Insert Rows' },
        { value: 'updateRows', name: 'Update Rows' },
        { value: 'deleteRows', name: 'Delete Rows' },
      ],
    },

    // Bulk Data operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'appendJson',
      description: 'Select the bulk operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['bulkData'],
        },
      },
      options: [
        { value: 'appendJson', name: 'Append JSON' },
        { value: 'appendCsv', name: 'Append CSV' },
        { value: 'updateAppendJson', name: 'Update/Append JSON' },
        { value: 'updateAppendCsv', name: 'Update/Append CSV' },
        { value: 'deleteJson', name: 'Delete JSON' },
        { value: 'deleteCsv', name: 'Delete CSV' },
      ],
    },

    // Table operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getTable',
      description: 'Select the table operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['table'],
        },
      },
      options: [
        { value: 'getTable', name: 'Get Table Details' },
        { value: 'getSchema', name: 'Get Table Schema' },
        { value: 'refreshTable', name: 'Refresh Table' },
      ],
    },

    // Data Flow operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'runDataFlow',
      description: 'Select the data flow operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['dataFlow'],
        },
      },
      options: [
        { value: 'runDataFlow', name: 'Run Data Flow' },
        { value: 'getDataFlow', name: 'Get Data Flow Details' },
      ],
    },

    // Schedule operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'triggerSchedule',
      description: 'Select the schedule operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['schedule'],
        },
      },
      options: [
        { value: 'triggerSchedule', name: 'Trigger Schedule' },
        { value: 'getSchedule', name: 'Get Schedule Details' },
      ],
    },

    // View operations
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'refreshCache',
      description: 'Select the view operation to perform',
      displayOptions: {
        show: {
          resourceGroup: ['view'],
        },
      },
      options: [
        { value: 'refreshCache', name: 'Refresh View Cache' },
      ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // RESOURCE ID FIELDS (with modeToggle for dropdowns)
    // ═══════════════════════════════════════════════════════════════════════════

    // Table ID (for data, bulkData, table resource groups)
    {
      key: 'tableId',
      label: 'Table',
      type: 'string',
      required: true,
      placeholder: 'Select or enter table ID',
      description: 'The table to work with',
      displayOptions: {
        show: {
          resourceGroup: ['data', 'bulkData', 'table'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTables',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Data Flow ID
    {
      key: 'dataFlowId',
      label: 'Data Flow',
      type: 'string',
      required: true,
      placeholder: 'Select or enter data flow ID',
      description: 'The data flow to work with',
      displayOptions: {
        show: {
          resourceGroup: ['dataFlow'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDataFlows',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Schedule ID
    {
      key: 'scheduleId',
      label: 'Schedule',
      type: 'string',
      required: true,
      placeholder: 'Select or enter schedule ID',
      description: 'The schedule to work with',
      displayOptions: {
        show: {
          resourceGroup: ['schedule'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getSchedules',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // View ID
    {
      key: 'viewId',
      label: 'View',
      type: 'string',
      required: true,
      placeholder: 'Select or enter view ID',
      description: 'The view to work with',
      displayOptions: {
        show: {
          resourceGroup: ['view'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getViews',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // OPERATION-SPECIFIC FIELDS
    // ═══════════════════════════════════════════════════════════════════════════

    // Max Rows (for read operations)
    {
      key: 'maxRows',
      label: 'Maximum Rows',
      type: 'number',
      required: false,
      default: 0,
      placeholder: '0',
      description: 'Maximum number of rows to return (0 = all rows)',
      displayOptions: {
        show: {
          operation: ['getRows'],
        },
      },
    },

    // Filter body for getRows
    {
      key: 'filterBody',
      label: 'Filter (Optional)',
      type: 'json',
      required: false,
      placeholder: '{}',
      description: 'Optional filter conditions for selecting rows',
      displayOptions: {
        show: {
          operation: ['getRows'],
        },
      },
    },

    // Data for insert operations (JSON array)
    {
      key: 'data',
      label: 'Data',
      type: 'json',
      required: true,
      placeholder: '[{"column1": "value1", "column2": "value2"}]',
      description: 'Array of row objects to insert (max 500 rows for non-bulk)',
      displayOptions: {
        show: {
          operation: ['insertRows', 'appendJson', 'updateAppendJson', 'deleteJson'],
        },
      },
    },

    // CSV Data for bulk CSV operations
    {
      key: 'csvData',
      label: 'CSV Data',
      type: 'string',
      required: true,
      typeOptions: {
        rows: 10,
      },
      placeholder: 'Column1,Column2,Column3\nvalue1,value2,value3',
      description: 'CSV data with header row',
      displayOptions: {
        show: {
          operation: ['appendCsv', 'updateAppendCsv', 'deleteCsv'],
        },
      },
    },

    // Data for update operations
    {
      key: 'updateData',
      label: 'Update Data',
      type: 'json',
      required: true,
      placeholder: '{"column1": "newValue"}',
      description: 'Object with column values to update',
      displayOptions: {
        show: {
          operation: ['updateRows'],
        },
      },
    },

    // Filter for update/delete operations
    {
      key: 'filter',
      label: 'Filter',
      type: 'json',
      required: true,
      placeholder: '{"condition": [{"columns": [{"name": "id", "comparator": "equals", "values": [1]}], "operator": "and"}]}',
      description: 'Filter conditions to match rows for update/delete',
      displayOptions: {
        show: {
          operation: ['updateRows', 'deleteRows'],
        },
      },
    },

    // Multiple rows handling for update/delete
    {
      key: 'multipleRows',
      label: 'Multiple Rows',
      type: 'options',
      required: true,
      default: 'first',
      description: 'How to handle when multiple rows match the filter',
      displayOptions: {
        show: {
          operation: ['updateRows', 'deleteRows'],
        },
      },
      options: [
        { value: 'first', name: 'First Match Only' },
        { value: 'all', name: 'All Matches' },
        { value: 'none', name: 'None (Cancel if Multiple)' },
      ],
    },

    // Refresh mode for table refresh
    {
      key: 'refreshMode',
      label: 'Refresh Mode',
      type: 'options',
      required: true,
      default: 'reload',
      description: 'How to refresh the table data',
      displayOptions: {
        show: {
          operation: ['refreshTable'],
        },
      },
      options: [
        { value: 'reload', name: 'Reload' },
        { value: 'rebuild', name: 'Rebuild' },
        { value: 'update', name: 'Update' },
        { value: 'append', name: 'Append' },
        { value: 'updateappend', name: 'Update & Append' },
      ],
    },

    // Error handling for bulk operations
    {
      key: 'errorHandling',
      label: 'On Error',
      type: 'options',
      required: false,
      default: 'stop',
      description: 'Action to perform if an error occurs during bulk operation',
      displayOptions: {
        show: {
          resourceGroup: ['bulkData'],
        },
      },
      options: [
        { value: 'stop', name: 'Stop Processing' },
        { value: 'continue', name: 'Continue Processing' },
      ],
    },

    // Include schema option for table details
    {
      key: 'includeSchema',
      label: 'Include Schema',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Include column schema in the response',
      displayOptions: {
        show: {
          operation: ['getTable'],
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // WAIT FOR COMPLETION OPTIONS (for async operations)
    // ═══════════════════════════════════════════════════════════════════════════

    // Wait for completion toggle
    {
      key: 'waitForCompletion',
      label: 'Wait for Completion',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Wait for the operation to complete before continuing to the next node',
      displayOptions: {
        show: {
          operation: ['triggerSchedule', 'runDataFlow', 'refreshTable', 'refreshCache'],
        },
      },
    },

    // Initial delay before first check
    {
      key: 'initialDelay',
      label: 'Initial Delay (seconds)',
      type: 'number',
      required: false,
      default: 0,
      placeholder: '0',
      description: 'Time to wait before the first completion check. Use this for long-running operations where you know the approximate duration (e.g., 2700 for 45 minutes).',
      displayOptions: {
        show: {
          operation: ['triggerSchedule', 'runDataFlow', 'refreshTable', 'refreshCache'],
          waitForCompletion: [true],
        },
      },
    },

    // Polling interval
    {
      key: 'pollingInterval',
      label: 'Polling Interval (seconds)',
      type: 'number',
      required: false,
      default: 5,
      placeholder: '5',
      description: 'How often to check for completion after the initial delay (in seconds).',
      displayOptions: {
        show: {
          operation: ['triggerSchedule', 'runDataFlow', 'refreshTable', 'refreshCache'],
          waitForCompletion: [true],
        },
      },
    },

    // Timeout for wait operations
    {
      key: 'waitTimeout',
      label: 'Timeout (seconds)',
      type: 'number',
      required: false,
      default: 300,
      placeholder: '300',
      description: 'Maximum total time to wait for completion including initial delay (in seconds). Default: 5 minutes.',
      displayOptions: {
        show: {
          operation: ['triggerSchedule', 'runDataFlow', 'refreshTable', 'refreshCache'],
          waitForCompletion: [true],
        },
      },
    },
  ],
};

export default schema;
